import { PageHero, FaqSection } from "@/components/site/sections";
import { Reveal, Overline } from "@/components/site/ui";
import { LeadForm } from "@/components/site/LeadForm";
import { WHATSAPP_NUMBER } from "@/lib/api";
import { IMAGES } from "@/data/content";
import { Phone, Mail, MapPin, Clock, Instagram, Facebook } from "lucide-react";

const INFO = [
  { icon: Phone, label: "Call / WhatsApp", value: `+91 ${WHATSAPP_NUMBER}` },
  { icon: Mail, label: "Email", value: "hello@spherioliving.com" },
  { icon: MapPin, label: "Office", value: "SCO No. 222, Shopping Square 1, Sector D, Sushant Golf City, Lucknow 226030" },
  { icon: Clock, label: "Hours", value: "Mon – Sat, 10am – 7pm" },
];

const SOCIALS = [
  { icon: Instagram, label: "Instagram", handle: "@spherioliving", href: "https://www.instagram.com/spherioliving/", testid: "contact-social-instagram" },
  { icon: Facebook, label: "Facebook", handle: "SpherioLiving", href: "https://www.facebook.com/p/SpherioLiving-61593647353806/", testid: "contact-social-facebook" },
];

export default function Contact() {
  return (
    <>
      <PageHero
        overline="Contact Us"
        title="Let’s talk about your home."
        subtitle="Reach out for a free consultation, a quote, or just to explore ideas. We’d love to hear from you."
        image={IMAGES.bedroom}
      />

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>Reach us</Overline>
            <h2 className="font-head text-4xl sm:text-5xl font-medium tracking-tight mt-5">
              Get in touch.
            </h2>
          </Reveal>
          <div className="mt-10 space-y-6">
            {INFO.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.07}>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1C1A17]/5 text-[#1C1A17]">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs tracking-[0.2em] uppercase text-[#5C574F]">{c.label}</div>
                    <div className="text-lg font-medium mt-1">{c.value}</div>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={INFO.length * 0.07}>
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1C1A17]/5 text-[#1C1A17]">
                  <Instagram className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-[#5C574F]">Follow us</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {SOCIALS.map((s) => (
                      <a
                        key={s.label}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Spherio Living on ${s.label}`}
                        data-testid={s.testid}
                        className="group inline-flex items-center gap-2 rounded-full border border-black/10 px-3.5 py-1.5 text-sm font-medium text-[#1C1A17] transition-[transform,color,border-color,background-color] duration-300 hover:-translate-y-0.5 hover:text-[#B8935A] hover:border-[#B8935A]"
                      >
                        <s.icon className="h-4 w-4" />
                        {s.handle}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="mt-10">
            <a
              href="https://share.google/MeXgUV7q34s8mu1fS"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-map-link"
              className="group relative block h-72 rounded-2xl overflow-hidden border border-black/10 shadow-lg shadow-black/5"
              aria-label="Open Spherio Living on Google Maps"
            >
              <iframe
                title="Spherio Living Private Limited — Sushant Golf City, Lucknow"
                src="https://maps.google.com/maps?q=Spherio+Living+Private+Limited,+Sector+D,+Shopping+Square+1,+Sushant+Golf+City,+Lucknow+226030&z=15&output=embed"
                className="absolute inset-0 h-full w-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              {/* Click-catcher so the whole card opens Google Maps in a new tab */}
              <span className="absolute inset-0 z-10" aria-hidden="true" />
              <span className="pointer-events-none absolute bottom-4 left-4 z-20 inline-flex items-center gap-2 rounded-full bg-[#1C1A17] px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-black/20 transition-transform duration-300 group-hover:-translate-y-0.5">
                <MapPin className="h-3.5 w-3.5 text-[#B8935A]" />
                Get directions
              </span>
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal>
            <div className="rounded-3xl border border-black/10 p-8 lg:p-10">
              <h3 className="font-head text-2xl font-medium">Send us a message</h3>
              <p className="text-[#5C574F] text-sm mt-2">We reply within 24 hours.</p>
              <div className="mt-8">
                <LeadForm source="contact" ctaLabel="Send Message" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqSection />
    </>
  );
}
